/**
 * Modelos de calidad (`/api/modelos`).
 *
 * El modelo es la PLANTILLA GENERAL: no lleva área ni ámbito. Porta el árbol de
 * componentes, que comparten todas sus versiones. Su estado gobierna qué se puede
 * editar; la descontinuación es manual y el backend la bloquea si hay ejecuciones
 * sin finalizar.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import EstructuraModelo from '@/interfaces/EstructuraModelo';
import { http } from '@/services/http'

/**
 * Consulta modelos · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns EstructuraModelo[]
 */
export async function getModelosService(url: string): Promise<EstructuraModelo[]> {
  const result = await http.get<EstructuraModelo[]>(url)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Consulta modelos · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns EstructuraModelo | null
 */
export async function getModeloByIdService(url: string, id_modelo: number): Promise<EstructuraModelo | null> {
  const result = await http.get<EstructuraModelo>(`${url.replace(/\/$/, '')}/${id_modelo}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea modelos.
 *
 * @returns EstructuraModelo | null
 */
export async function crearModeloService(
  url: string,
  modelo: Omit<EstructuraModelo, 'id_modelo'>
): Promise<EstructuraModelo | null> {
  const payload: Record<string, unknown> = {
    nombre: modelo.nombre,
    id_tipo_modelo: modelo.id_tipo_modelo,
    fecha_creacion: modelo.fecha_creacion,
    fecha_descontinuado: modelo.fecha_descontinuado ?? null,
    niveles: Array.isArray(modelo.niveles) ? JSON.stringify(modelo.niveles) : modelo.niveles ?? null,
    escala_max: modelo.escala_max ?? 5,
    vigencia_inicio: modelo.vigencia_inicio ?? null,
    vigencia_fin: modelo.vigencia_fin ?? null,
    acuerdo: modelo.acuerdo?.trim() || null
  }
  if (modelo.es_historico) payload.es_historico = true

  const result = await http.post<EstructuraModelo>(url, payload)
  if (!result.ok) return null
  return result.data ?? null
}

/**
 * Actualiza modelos.
 *
 * @returns EstructuraModelo | null
 */
export async function ActualizarModeloService(
  url: string,
  id_modelo: number,
  modelo: Partial<EstructuraModelo>
): Promise<EstructuraModelo | null> {
  const payload = {
    nombre: modelo.nombre,
    id_tipo_modelo: modelo.id_tipo_modelo,
    fecha_creacion: modelo.fecha_creacion,
    fecha_descontinuado: modelo.fecha_descontinuado ?? null,
    niveles: Array.isArray(modelo.niveles) ? JSON.stringify(modelo.niveles) : modelo.niveles ?? null,
    escala_max: modelo.escala_max ?? 5,
    vigencia_inicio: modelo.vigencia_inicio ?? null,
    vigencia_fin: modelo.vigencia_fin ?? null,
    acuerdo: modelo.acuerdo?.trim() || null
  }
  const result = await http.put<EstructuraModelo>(`${url.replace(/\/$/, '')}/${id_modelo}`, payload)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Elimina modelos · petición `DELETE`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function eliminarModeloService(url: string, id_modelo: number): Promise<boolean> {
  const result = await http.delete(`${url.replace(/\/$/, '')}/${id_modelo}`)
  return result.ok
}

/**
 * Cambia modelos · petición `PATCH`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns EstructuraModelo | null
 */
export async function cambiarEstadoModeloService(
  url: string,
  id_modelo: number,
  estado: EstructuraModelo['estado']
): Promise<EstructuraModelo | null> {
  const result = await http.patch<EstructuraModelo>(`${url.replace(/\/$/, '')}/${id_modelo}/estado`, { estado })
  return result.ok ? (result.data ?? null) : null
}

/**
 * Operación sobre modelos · petición `POST`.
 *
 * @returns { ok: boolean; data?: EstructuraModelo; message?: string }
 */
export async function descontinuarModeloService(
  url: string,
  id_modelo: number
): Promise<{ ok: boolean; data?: EstructuraModelo; message?: string }> {
  const result = await http.post<EstructuraModelo>(`${url.replace(/\/$/, '')}/${id_modelo}/descontinuar`)
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? `HTTP ${result.status}`
    return { ok: false, message: msg }
  }
  return { ok: true, data: result.data }
}

/**
 * Clona modelos · petición `POST`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns EstructuraModelo | null
 */
export async function clonarModeloService(
  url: string,
  id_modelo: number,
  // El clon copia SOLO la estructura: no nace con versión ni hereda indicadores.
  opciones?: { ordinales?: string[]; excluir_ordinales?: string[]; nuevo_nombre?: string }
): Promise<EstructuraModelo | null> {
  const result = await http.post<EstructuraModelo>(`${url.replace(/\/$/, '')}/${id_modelo}/clonar`, opciones ?? {})
  return result.ok ? (result.data ?? null) : null
}

/**
 * Consulta modelos · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns { nodes: any[]; edges: any[] } | null
 */
export async function getModeloGraphService(
  url: string,
  id_modelo: number
): Promise<{ nodes: any[]; edges: any[] } | null> {
  const result = await http.get<{ nodes: any[]; edges: any[] }>(`${url.replace(/\/$/, '')}/${id_modelo}/graph`)
  return result.ok ? (result.data ?? null) : null
}
